import {
    VerifiedOutlined as VerifiedOutlinedIcon,
    WorkspacePremiumRounded as WorkspacePremiumRoundedIcon,
} from '@mui/icons-material';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { CertificateHistoryItem } from '~/shared/data-contract/certificate-history';

import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { useCertificateHistoryQuery } from '../../../query-hooks/users/certificate-history';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserCertificateUrl, editUserCertificateUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';

const CertificateHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="certification.main"
            icon={VerifiedOutlinedIcon}
            text={translate('pages.user-certificates.caption')}
        />
    );
};

type CertificateProps = {
    readonly userId: string;
};

type CertificateHistoryContentProps = {
    readonly canUserAddCertificateHistory?: boolean;
    readonly canUserEditCertificateHistory?: boolean;
    readonly certificateHistory?: ReadonlyArray<CertificateHistoryItem>;
    readonly userId?: string;
};

export const CertificateHistoryContent: FunctionComponent<CertificateHistoryContentProps> = ({
    canUserAddCertificateHistory = false,
    canUserEditCertificateHistory = false,
    certificateHistory = [],
    userId,
}) => {
    const { actions, translate } = useContext(ApplicationContext);

    const [showAll, setShowAll] = useState(false);
    const hasCertificateHistory = certificateHistory.length > 0;
    const slicedCertificates = showAll ? certificateHistory : certificateHistory.slice(0, 2);

    const headlineActions = canUserAddCertificateHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserCertificateUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditCertificateHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserCertificateUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        <HistoryItemListWrapper
            anchorId="certificates"
            headline={translate('pages.recruiter-profile.background.certificates.title')}
            headlineActions={headlineActions}
        >
            {hasCertificateHistory ? (
                slicedCertificates.map((certificate) => (
                    <HistoryItem
                        key={certificate.id}
                        caption={<CertificateHistoryCaption />}
                        dateFrom={certificate.acquiredDate}
                        dateTo={certificate.expiryDate}
                        skills={!is.nullOrUndefined(certificate.skill) ? [certificate.skill] : []}
                        skillsHeadline={translate('pages.user-certificates.skills-list-headline')}
                        subTitle={[certificate.issuer]}
                        title={certificate.certificateName}
                        onClickEdit={editAction(certificate.id)}
                    />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<WorkspacePremiumRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.background.certificates.placeholder')}
                />
            )}
            {certificateHistory.length > 2 && <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />}
        </HistoryItemListWrapper>
    );
};

export const Certificates: FunctionComponent<CertificateProps> = ({ userId }: CertificateProps) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: certificateHistory, isFetching: certificateHistoryIsFetching } = useCertificateHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || certificateHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <CertificateHistoryContent
            canUserAddCertificateHistory={userHistoryOptions?.canEditHistory}
            canUserEditCertificateHistory={userHistoryOptions?.canEditHistory}
            certificateHistory={certificateHistory}
            userId={userId}
        />
    );
};
